import React from "react";

/**
 * EventRegistrationPage.jsx
 * Machine Cinema × NewZenAlpha — React single-file page (no external libs required)
 * - Plain CSS classes (see <style> at bottom) so you can drop this into any CRA/Vite/Next project.
 * - Props let you customize logos, copy, Google Form URL, agenda, etc.
 * - Fully responsive, Luma-style hero + features + agenda + Google Form + map + FAQ.
 *
 * Usage:
 * <EventRegistrationPage
 *   machineCinemaLogo="/assets/machine-cinema.png"
 *   newZenAlphaLogo="/assets/newzenalpha.png"
 *   formUrl="https://docs.google.com/forms/d/e/REPLACE_WITH_YOUR_EMBED/viewform?embedded=true"
 * />
 */

export default function EventRegistrationPage({
  machineCinemaLogo = "/machinecinema.png",
  newZenAlphaLogo = "/newzenalpha.png",
  title = "Learn. Create. Play. — Live Workshop & Community Meetup",
  subTitle =
    "A hands‑on evening exploring AI‑assisted filmmaking, creative automation, and practical workflows. Come for the demos, stay for the collabs.",
  dateLabel = "Sat, 21 Sept 2025",
  timeLabel = "4:00 PM – 7:30 PM",
  placeLabel = "Bengaluru, India & Online",
  formUrl = "https://docs.google.com/forms/d/e/1FAIpQLScqnPFhj76j37TXbktvfQHNXS2SB2OcCRUhEPDTWc1XZitP4Q/viewform?usp=header",
  mapEmbed =
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3890.0!2d77.0!3d12.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z12KZ!5e0!3m2!1sen!2sin!4v1690000000000",
  agenda = [
    { time: "4:00 – 4:20 PM", text: "Check‑in & intros" },
    { time: "4:20 – 5:10 PM", text: "Keynote & live demo: AI pre‑viz → edit → sound" },
    { time: "5:10 – 6:10 PM", text: "Hands‑on sprint (teams)" },
    { time: "6:10 – 6:40 PM", text: "Project share & feedback" },
    { time: "6:40 – 7:30 PM", text: "Networking + Q&A" },
  ],
  faqs = [
    { q: "Is it free?", a: "Yes, but seats are limited." },
    { q: "Do I need experience?", a: "No, we cover fundamentals." },
    { q: "Can I bring a friend?", a: "Yes, just ask them to register." },
  ],
}) {
  React.useEffect(() => {
    const yearEl = document.getElementById("mc-nza-year");
    if (yearEl) yearEl.textContent = new Date().getFullYear();
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="mc-nza-root">
      <header className="mc-nza-header">
        <div className="mc-nza-container mc-nza-nav">
          <div className="mc-nza-brand">
            <img src={machineCinemaLogo} alt="Machine Cinema" />
            <div className="divider" aria-hidden="true" />
            <img src={newZenAlphaLogo} alt="NewZenAlpha" />
          </div>
          <nav className="mc-nza-cta">
            <button className="btn btn--secondary" onClick={() => scrollToId("agenda")}>Agenda</button>
            <button className="btn btn--secondary" onClick={() => scrollToId("venue")}>Venue</button>
            <button className="btn btn--primary" onClick={() => scrollToId("register")}>Register</button>
          </nav>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="hero">
          <div className="mc-nza-container">
            <div className="wrap">
              <div className="hero-content">
              <span className="badge">Machine Cinema × NewZenAlpha Presents</span>
              <h1 className="title">{title}</h1>
              <p className="subtitle">{subTitle}</p>
              <div className="meta">
                <span className="chip">🗓️ {dateLabel}</span>
                <span className="chip">⏰ {timeLabel}</span>
                <span className="chip">📍 {placeLabel}</span>
              </div>
                <div className="hero-actions">
                  <button className="btn btn--primary btn--large" onClick={() => scrollToId("register")}>
                    <span>Register Now</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                  <button className="btn btn--outline btn--large" onClick={() => scrollToId("about")}>
                    <span>Learn More</span>
                  </button>
              </div>
              {/* <div className="hero-card">
                  <div className="hero-card-header">
                    <h3>What you'll get</h3>
                  </div>
                  <ul className="hero-card-list">
                  <li>• Practical demos of AI + film tools</li>
                  <li>• Creative sprints & feedback loops</li>
                  <li>• Access to project templates & resources</li>
                  <li>• Networking with makers & studios</li>
                </ul>
              </div> */}
            </div>

              <div className="hero-sidebar">
                <div className="card event-snapshot">
              <h2 className="section-title no-margin">Event Snapshot</h2>
              <div className="grid facts">
                <InfoCard label="Format" value="Hybrid (In‑person + Online)" />
                <InfoCard label="Level" value="Beginner → Advanced tracks" />
                <InfoCard label="Bring" value="Laptop & curiosity" />
                <InfoCard label="Price" value="Free — Registration required" />
              </div>
            </div>
                
                {/* Company Logos Showcase - Below Event Snapshot */}
                <div className="hero-logos">
                  <div className="logo-showcase">
                    <div className="logo-item">
                      <img src={machineCinemaLogo} alt="Machine Cinema" className="company-logo" />
                      <span className="logo-label">Machine Cinema</span>
                    </div>
                    <div className="logo-divider">
                      <span>×</span>
                    </div>
                    <div className="logo-item">
                      <img src={newZenAlphaLogo} alt="NewZenAlpha" className="company-logo" />
                      <span className="logo-label">NewZenAlpha</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

                {/* ABOUT & AGENDA - Horizontal Layout */}
        <section id="about" className="section">
          <div className="mc-nza-container">
            <div className="horizontal-sections">
                            {/* Agenda Section - Timeline Style */}
              <div className="section-column">
                <div className="section-header">
                  <h2 className="section-title">Event Timeline</h2>
                  <p className="section-subtitle">Your journey through the day</p>
            </div>
                <div className="simple-agenda">
                  <div className="agenda-item">
                    <span className="agenda-time">9:00 AM - 9:30 AM</span>
                    <span className="agenda-text">Registration & Welcome</span>
          </div>
                  <div className="agenda-item">
                    <span className="agenda-time">9:30 AM - 10:15 AM</span>
                    <span className="agenda-text">Opening Keynote</span>
                  </div>
                  <div className="agenda-item">
                    <span className="agenda-time">10:15 AM - 11:00 AM</span>
                    <span className="agenda-text">Panel Discussion</span>
                  </div>
                  <div className="agenda-item">
                    <span className="agenda-time">11:00 AM - 12:00 PM</span>
                    <span className="agenda-text">Workshop & Networking</span>
                  </div>
                </div>
              </div>

                            {/* Why Attend Section - Benefits Cards */}
              <div className="section-column">
                <div className="section-header">
                  <h2 className="section-title">Why Join Us?</h2>
                  <p className="section-subtitle">Unlock your creative potential</p>
                </div>
                <div className="benefits-grid">
                  <div className="benefit-card">
                    <div className="benefit-icon">🎬</div>
                    <div className="benefit-content">
                      <h3>AI Storytelling</h3>
                      <p>Transform ideas into visual narratives with cutting-edge AI tools</p>
                    </div>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">🛠️</div>
                    <div className="benefit-content">
                      <h3>Hands-on Learning</h3>
                      <p>Master practical techniques through interactive workshops</p>
                    </div>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">🤝</div>
                    <div className="benefit-content">
                      <h3>Creative Network</h3>
                      <p>Connect with industry leaders and fellow creators</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* REGISTER */}
        <section id="register" className="section">
          <div className="mc-nza-container">
            <div className="register-content">
              <div className="register-info">
                <div className="section-header">
              <h2 className="section-title">Register</h2>
                  <p className="section-subtitle">
                    Fill the form to secure your spot. You'll receive a confirmation email with venue details and livestream link. Seats are limited.
              </p>
              </div>
                <div className="register-actions">
                  <button className="btn btn--primary" onClick={() => scrollToId("register-form")}>
                    <span>Jump to Form</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                  <button className="btn btn--outline" onClick={() => scrollToId("faq")}>
                    <span>Read FAQs</span>
                  </button>
                </div>
                <div className="card privacy-notice">
                  <div className="privacy-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                <strong>Privacy</strong>
                  </div>
                <p className="muted">Your details are used only for event coordination. No spam, ever.</p>
              </div>
            </div>
              <div id="register-form" className="register-form-container">
                <div className="form-header">
                  <h3>Registration Form</h3>
                  <p className="muted">Complete the form below to secure your spot</p>
                </div>
                <div className="embed" aria-label="Registration form">
              <iframe title="Event Registration" src={formUrl}>Loading…</iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VENUE */}
        <section id="venue" className="section section--alt">
          <div className="mc-nza-container">
            <div className="venue-content">
              <div className="venue-info">
                <div className="section-header">
              <h2 className="section-title">Venue</h2>
                  <p className="section-subtitle">Join us in the heart of Bengaluru's tech district</p>
                </div>
                <div className="card venue-details">
                  <div className="venue-header">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                <strong>Downtown Bengaluru</strong>
                  </div>
                <p className="muted">Exact address will be shared with confirmed attendees. Accessible by metro and ride‑share. Parking available nearby.</p>
                  <div className="venue-features">
                    <div className="venue-feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      </svg>
                      <span>Metro accessible</span>
              </div>
                    <div className="venue-feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3h18v18H3zM9 9h6v6H9z"/>
                      </svg>
                      <span>Parking available</span>
                    </div>
                    <div className="venue-feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                      </svg>
                      <span>Wheelchair accessible</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="venue-map">
                <div className="map-header">
                  <h3>Location</h3>
                  <p className="muted">Bengaluru, Karnataka, India</p>
            </div>
            <div className="embed" aria-label="Map">
              <iframe title="Google Map" src={mapEmbed} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="section">
          <div className="mc-nza-container">
            <div className="section-header">
              <h2 className="section-title">Quick Questions</h2>
              <p className="section-subtitle">Common questions answered</p>
            </div>
            <div className="simple-faq">
              {faqs.map((f, i) => (
                <div className="faq-item-simple" key={i}>
                  <div className="faq-q">{f.q}</div>
                  <div className="faq-a">{f.a}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="mc-nza-footer">
        <div className="mc-nza-container mc-nza-footwrap">
          <div className="logos">
            <img src={machineCinemaLogo} alt="Machine Cinema" />
            <span>×</span>
            <img src={newZenAlphaLogo} alt="NewZenAlpha" />
          </div>
          <div className="footer-text">
            © <span id="mc-nza-year" /> Machine Cinema & NewZenAlpha
          </div>
        </div>
      </footer>

      {/* Optimized Event Registration CSS */}
      <style>{`
        :root { 
          --bg: #ffffff; 
          --bg-alt: #f8fafc;
          --bg-accent: #f1f5f9;
          --fg: #1e293b; 
          --muted: #64748b; 
          --accent: #3b82f6; 
          --accent-light: #dbeafe;
          --accent-dark: #1e40af;
          --success: #10b981;
          --border: #e2e8f0;
          --border-light: #f1f5f9;
          --shadow: 0 1px 3px rgba(0,0,0,0.1);
          --shadow-lg: 0 4px 6px rgba(0,0,0,0.05);
          --radius: 6px;
          --radius-lg: 8px;
        }
        
        .mc-nza-root { 
          color: var(--fg); 
          background: 
            radial-gradient(circle at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(99, 102, 241, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 90% 20%, rgba(168, 85, 247, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 10% 90%, rgba(79, 70, 229, 0.06) 0%, transparent 50%),
            #f8fafc;
          background-size: 400% 400%, 300% 300%, 500% 500%, 350% 350%, 450% 450%, 100% 100%;
          animation: 
            meshMove1 20s ease infinite,
            meshMove2 25s ease infinite,
            meshMove3 30s ease infinite,
            meshMove4 35s ease infinite,
            meshMove5 40s ease infinite;
          min-height: 100dvh; 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.5;
          font-size: 15px;
        }
        
        @keyframes meshMove1 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 100% 50%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          50% { background-position: 50% 100%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          75% { background-position: 0% 50%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
        }
        
        @keyframes meshMove2 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 100% 30%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          50% { background-position: 0% 0%, 30% 100%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          75% { background-position: 0% 0%, 0% 70%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
        }
        
        @keyframes meshMove3 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 0% 0%, 80% 20%, 0% 0%, 0% 0%, 0% 0%; }
          50% { background-position: 0% 0%, 0% 0%, 20% 80%, 0% 0%, 0% 0%, 0% 0%; }
          75% { background-position: 0% 0%, 0% 0%, 60% 60%, 0% 0%, 0% 0%, 0% 0%; }
        }
        
        @keyframes meshMove4 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 0% 0%, 0% 0%, 70% 40%, 0% 0%, 0% 0%; }
          50% { background-position: 0% 0%, 0% 0%, 0% 0%, 40% 70%, 0% 0%, 0% 0%; }
          75% { background-position: 0% 0%, 0% 0%, 0% 0%, 10% 10%, 0% 0%, 0% 0%; }
        }
        
        @keyframes meshMove5 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 90% 10%, 0% 0%; }
          50% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 10% 90%, 0% 0%; }
          75% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 50% 50%, 0% 0%; }
        }
        
        .mc-nza-container { 
          width: min(1400px, 95vw); 
          margin: 0 auto; 
          padding: 0 20px;
        }
        
        .grid { 
          display: grid; 
          gap: 20px; 
        }
        
        .muted { 
          color: var(--muted); 
        }

        /* Compact Header */
        .mc-nza-header { 
          position: sticky; 
          top: 0; 
          z-index: 50; 
          backdrop-filter: blur(8px); 
          background: rgba(255,255,255,0.95); 
          border-bottom: 1px solid var(--border-light);
        }
        
        .mc-nza-nav { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          padding: 12px 0; 
        }
        
        .mc-nza-brand { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
        }
        
        .mc-nza-brand img { 
          height: 36px; 
          width: auto; 
          object-fit: contain;
        }
        
        .mc-nza-brand .divider { 
          width: 1px; 
          height: 20px; 
          background: var(--border); 
        }
        
        .mc-nza-cta { 
          display: flex; 
          gap: 12px; 
        }

        /* Compact Button System */
        .btn { 
          display: inline-flex; 
          align-items: center; 
          justify-content: center;
          gap: 6px; 
          border: 1px solid var(--border); 
          background: var(--bg); 
          padding: 10px 20px; 
          border-radius: var(--radius); 
          color: var(--fg); 
          font-weight: 500; 
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        
        .btn:hover { 
          background: var(--bg-alt);
          border-color: var(--accent);
        }
        
        .btn--primary { 
          background: var(--accent); 
          border-color: var(--accent); 
          color: white;
        }
        
        .btn--primary:hover { 
          background: var(--accent-dark);
          border-color: var(--accent-dark);
        }
        
        .btn--secondary {
          background: var(--bg-alt);
          border-color: var(--border);
          color: var(--muted);
        }
        
        .btn--secondary:hover {
          background: var(--bg);
          color: var(--fg);
        }
        
        .btn--outline {
          background: transparent;
          border: 1px solid var(--accent);
          color: var(--accent);
        }
        
        .btn--outline:hover {
          background: var(--accent);
          color: white;
        }
        
        .btn--large {
          padding: 14px 28px;
          font-size: 15px;
        }

        /* Compact Hero Section */
        .hero { 
          padding: 40px 0 30px;
          background: 
            radial-gradient(circle at 30% 20%, rgba(139, 92, 246, 0.2) 0%, transparent 60%),
            radial-gradient(circle at 70% 80%, rgba(99, 102, 241, 0.15) 0%, transparent 60%),
            radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 60%),
            rgba(248, 250, 252, 0.8);
          background-size: 300% 300%, 400% 400%, 500% 500%, 100% 100%;
          animation: 
            heroMesh1 25s ease infinite,
            heroMesh2 30s ease infinite,
            heroMesh3 35s ease infinite;
          position: relative;
        }
        
        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 80% 30%, rgba(168, 85, 247, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 20% 70%, rgba(79, 70, 229, 0.08) 0%, transparent 50%);
          background-size: 350% 350%, 450% 450%;
          animation: 
            heroAccent1 40s ease infinite,
            heroAccent2 45s ease infinite;
          pointer-events: none;
        }
        
        @keyframes heroMesh1 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 100% 30%, 0% 0%, 0% 0%, 0% 0%; }
          50% { background-position: 30% 100%, 0% 0%, 0% 0%, 0% 0%; }
          75% { background-position: 70% 70%, 0% 0%, 0% 0%, 0% 0%; }
        }
        
        @keyframes heroMesh2 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 80% 20%, 0% 0%, 0% 0%; }
          50% { background-position: 0% 0%, 20% 80%, 0% 0%, 0% 0%; }
          75% { background-position: 0% 0%, 60% 60%, 0% 0%, 0% 0%; }
        }
        
        @keyframes heroMesh3 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 0% 0%, 70% 40%, 0% 0%; }
          50% { background-position: 0% 0%, 0% 0%, 40% 70%, 0% 0%; }
          75% { background-position: 0% 0%, 0% 0%, 10% 10%, 0% 0%; }
        }
        
        @keyframes heroAccent1 {
          0%, 100% { background-position: 0% 0%, 0% 0%; }
          25% { background-position: 100% 50%, 0% 0%; }
          50% { background-position: 50% 100%, 0% 0%; }
          75% { background-position: 0% 50%, 0% 0%; }
        }
        
        @keyframes heroAccent2 {
          0%, 100% { background-position: 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 90% 10%; }
          50% { background-position: 0% 0%, 10% 90%; }
          75% { background-position: 0% 0%, 50% 50%; }
        }
        
        /* Company Logos Showcase - Below Event Snapshot */
        .hero-logos {
          margin-top: 24px;
        }
        
        .logo-showcase {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          background: var(--bg);
          padding: 20px 24px;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--border);
        }
        
        .logo-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        
        .company-logo {
          height: 60px;
          width: auto;
          object-fit: contain;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }
        
        .logo-label {
          font-size: 14px;
          font-weight: 600;
          color: var(--muted);
          text-align: center;
        }
        
        .logo-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: var(--accent-light);
          border-radius: 50%;
          color: var(--accent);
          font-size: 20px;
          font-weight: 700;
        }
        
        .hero .wrap { 
          display: grid; 
          grid-template-columns: 1.3fr 1fr; 
          gap: 40px; 
          align-items: start;
        }
        
        .hero-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .badge { 
          display: inline-flex; 
          align-items: center; 
          gap: 6px; 
          background: var(--accent-light); 
          color: var(--accent); 
          padding: 4px 10px; 
          border-radius: 16px; 
          font-weight: 500;
          font-size: 13px;
          width: fit-content;
        }
        
        .title { 
          font-weight: 700; 
          font-size: clamp(28px, 4vw, 48px); 
          line-height: 1.2; 
          margin: 0;
          color: var(--fg);
        }
        
        .subtitle { 
          color: var(--muted); 
          font-size: clamp(16px, 1.8vw, 20px); 
          max-width: 55ch;
          line-height: 1.4;
          font-weight: 400;
        }
        
        .meta { 
          display: flex; 
          flex-wrap: wrap; 
          gap: 12px; 
          margin: 0;
        }
        
        .chip { 
          display: inline-flex; 
          align-items: center; 
          gap: 6px; 
          padding: 6px 12px; 
          border-radius: 16px; 
          background: var(--bg-alt); 
          border: 1px solid var(--border); 
          color: var(--muted); 
          font-weight: 500;
          font-size: 13px;
        }
        
        .hero-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .hero-card { 
          background: var(--bg); 
          border: 1px solid var(--border); 
          border-radius: var(--radius-lg); 
          padding: 20px; 
          box-shadow: var(--shadow-lg);
        }
        
        .hero-card-header h3 {
          margin: 0 0 12px 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--fg);
        }
        
        .hero-card-list { 
          list-style: none; 
          margin: 0; 
          padding: 0; 
          display: flex; 
          flex-direction: column;
          gap: 8px; 
          color: var(--muted);
        }
        
        .hero-card-list li {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        /* Compact Cards */
        .card { 
          background: var(--bg); 
          border: 1px solid var(--border); 
          border-radius: var(--radius-lg); 
          padding: 20px; 
          box-shadow: var(--shadow-lg);
          transition: all 0.2s ease;
        }
        
        .card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        
        .section-title { 
          font-size: clamp(24px, 2.5vw, 36px); 
          font-weight: 700; 
          margin: 0 0 12px 0;
          color: var(--fg);
          line-height: 1.2;
        }
        
        .section-subtitle {
          color: var(--muted);
          font-size: 15px;
          margin: 0 0 20px 0;
          line-height: 1.4;
          font-weight: 400;
        }
        
        .section-header {
          text-align: center;
          margin-bottom: 32px;
        }
        
        .no-margin { 
          margin: 0 0 16px 0; 
        }

        /* Compact Section Styling */
        .section {
          padding: 50px 0;
        }
        
        .section--alt {
          background: 
            radial-gradient(circle at 60% 40%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, rgba(99, 102, 241, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.04) 0%, transparent 50%),
            rgba(248, 250, 252, 0.6);
          background-size: 400% 400%, 300% 300%, 500% 500%, 100% 100%;
          animation: 
            sectionMesh1 45s ease infinite,
            sectionMesh2 50s ease infinite,
            sectionMesh3 55s ease infinite;
        }
        
        @keyframes sectionMesh1 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 100% 40%, 0% 0%, 0% 0%, 0% 0%; }
          50% { background-position: 40% 100%, 0% 0%, 0% 0%, 0% 0%; }
          75% { background-position: 60% 60%, 0% 0%, 0% 0%, 0% 0%; }
        }
        
        @keyframes sectionMesh2 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 70% 30%, 0% 0%, 0% 0%; }
          50% { background-position: 0% 0%, 30% 70%, 0% 0%, 0% 0%; }
          75% { background-position: 0% 0%, 50% 50%, 0% 0%, 0% 0%; }
        }
        
        @keyframes sectionMesh3 {
          0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
          25% { background-position: 0% 0%, 0% 0%, 80% 20%, 0% 0%; }
          50% { background-position: 0% 0%, 0% 0%, 20% 80%, 0% 0%; }
          75% { background-position: 0% 0%, 0% 0%, 60% 60%, 0% 0%; }
        }

        /* Horizontal Sections Layout */
        .horizontal-sections {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: start;
        }
        
        .section-column {
          display: flex;
          flex-direction: column;
        }
        
        .section-column .section-header {
          margin-bottom: 24px;
          text-align: left;
        }

        /* Simple Agenda Style */
        .simple-agenda {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .agenda-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid rgba(59, 130, 246, 0.1);
        }
        
        .agenda-item:last-child {
          border-bottom: none;
        }
        
        .agenda-time {
          font-weight: 600;
          font-size: 13px;
          color: var(--accent);
          min-width: 120px;
          flex-shrink: 0;
        }
        
        .agenda-text {
          font-size: 14px;
          color: var(--fg);
          font-weight: 500;
        }

        /* Benefits Cards Style */
        .benefits-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .benefit-card {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(59, 130, 246, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        
        .benefit-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent), rgba(99, 102, 241, 0.8));
        }
        
        .benefit-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.2);
        }
        
        .benefit-icon {
          font-size: 24px;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1));
          border-radius: 12px;
          flex-shrink: 0;
        }
        
        .benefit-content h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--fg);
          margin: 0 0 8px 0;
        }
        
        .benefit-content p {
          font-size: 14px;
          color: var(--muted);
          margin: 0;
          line-height: 1.5;
        }

        /* Simple FAQ Style */
        .simple-faq {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .faq-item-simple {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.1);
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }
        
        .faq-q {
          font-weight: 600;
          font-size: 14px;
          color: var(--fg);
          flex: 1;
        }
        
        .faq-a {
          font-size: 13px;
          color: var(--muted);
          text-align: right;
          margin-left: 16px;
        }
        
        .slot:hover {
          transform: none;
        }
        
        .slot-time {
          font-weight: 600;
          color: var(--accent);
          font-size: 14px;
          margin-bottom: 4px;
          display: block;
        }
        
        .slot-content {
          color: var(--fg);
          line-height: 1.5;
          font-size: 14px;
          margin: 0;
        }

        /* Register Section - Optimized Layout */
        .register-content {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 40px;
          align-items: start;
        }
        
        .register-info {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        
        .register-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .privacy-notice {
          background: var(--accent-light);
          border: 1px solid var(--accent);
          border-radius: var(--radius);
          padding: 16px;
        }
        
        .privacy-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }
        
        .privacy-header svg {
          color: var(--accent);
        }
        
        .privacy-header strong {
          color: var(--accent);
          font-weight: 600;
          font-size: 14px;
        }
        
        .privacy-notice p {
          font-size: 13px;
          margin: 0;
        }
        
        .register-form-container {
          background: var(--bg);
          border-radius: var(--radius-lg);
          padding: 24px;
          border: 1px solid var(--border);
          box-shadow: var(--shadow-lg);
        }
        
        .form-header {
          margin-bottom: 20px;
        }
        
        .form-header h3 {
          margin: 0 0 6px 0;
          font-size: 20px;
          font-weight: 600;
          color: var(--fg);
        }
        
        .form-header p {
          font-size: 14px;
          margin: 0;
        }

        /* Venue Section - Compact Layout */
        .venue-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: start;
        }
        
        .venue-details {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          padding: 20px;
          box-shadow: var(--shadow-lg);
        }
        
        .venue-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }
        
        .venue-header svg {
          color: var(--accent);
        }
        
        .venue-header strong {
          font-size: 18px;
          font-weight: 600;
          color: var(--fg);
        }
        
        .venue-features {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 16px;
        }
        
        .venue-feature {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--muted);
          font-size: 13px;
        }
        
        .venue-feature svg {
          color: var(--accent);
        }
        
        .map-header {
          margin-bottom: 16px;
        }
        
        .map-header h3 {
          margin: 0 0 6px 0;
          font-size: 20px;
          font-weight: 600;
          color: var(--fg);
        }
        
        .map-header p {
          font-size: 14px;
          margin: 0;
        }

        /* FAQ Section - Professional Layout */
        .faq { 
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .faq-item {
          padding: 24px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          border-left: 4px solid var(--accent);
        }
        
        .faq-item:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }
        
        .faq-question {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .faq-question svg {
          color: var(--accent);
          flex-shrink: 0;
          margin-top: 2px;
        }
        
        .faq-question strong {
          font-size: 17px;
          font-weight: 600;
          color: var(--fg);
          line-height: 1.4;
        }
        
        .faq-item p {
          font-size: 15px;
          margin: 0;
          line-height: 1.6;
          color: var(--muted);
          padding-left: 28px;
        }

        /* Compact Embeds */
        .embed { 
          position: relative; 
          width: 100%; 
          padding-top: 60%; 
          border-radius: var(--radius-lg); 
          overflow: hidden; 
          border: 1px solid var(--border); 
          box-shadow: var(--shadow-lg);
        }
        
        .embed iframe { 
          position: absolute; 
          inset: 0; 
          width: 100%; 
          height: 100%; 
          border: 0; 
          background: #fff;
        }

        /* Compact Footer */
        .mc-nza-footer { 
          border-top: 1px solid var(--border-light); 
          padding: 30px 0; 
          color: var(--muted);
          background: var(--bg-alt);
        }
        
        .mc-nza-footwrap { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          gap: 20px; 
          flex-wrap: wrap;
        }
        
        .mc-nza-footwrap .logos { 
          display: flex; 
          align-items: center; 
          gap: 12px;
        }
        
        .mc-nza-footwrap img { 
          height: 28px;
        }
        
        .footer-text {
          font-size: 13px;
          color: var(--muted);
        }

        /* Event-specific optimizations */
        .event-snapshot {
          background: var(--bg-accent);
          border: 1px solid var(--border);
        }
        
        .facts {
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        
        .facts .card {
          padding: 12px;
          background: var(--bg);
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .mc-nza-container {
            width: 95vw;
            padding: 0 16px;
          }
        }
        
        @media (max-width: 1024px) {
          .hero .wrap { 
            grid-template-columns: 1fr;
            gap: 30px;
          }
          
          .register-content,
          .venue-content {
            grid-template-columns: 1fr;
            gap: 30px;
          }
          
          .horizontal-sections {
            grid-template-columns: 1fr;
            gap: 40px;
          }
          
          .agenda-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .agenda-time {
            min-width: auto;
          }
          
          .benefit-card {
            padding: 16px;
            gap: 12px;
          }
          
          .benefit-icon {
            width: 40px;
            height: 40px;
            font-size: 20px;
          }
          
          .faq-item-simple {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            padding: 12px 16px;
          }
          
          .faq-a {
            text-align: left;
            margin-left: 0;
          }
          
          .faq {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
        
        @media (max-width: 768px) {
          .hero {
            padding: 30px 0 20px;
          }
          
          .section {
            padding: 40px 0;
          }
          
          .hero-logos {
            margin-top: 20px;
          }
          
          .logo-showcase {
            gap: 20px;
            padding: 16px 20px;
          }
          
          .company-logo {
            height: 44px;
          }
          
          .logo-label {
            font-size: 12px;
          }
          
          .logo-divider {
            width: 28px;
            height: 28px;
            font-size: 14px;
          }
          
          .hero-actions {
            flex-direction: column;
          }
          
          .register-actions {
            flex-direction: column;
          }
          
          .mc-nza-cta { 
            flex-direction: column;
            gap: 8px;
          }
          
          .mc-nza-cta .btn { 
            width: 100%; 
            justify-content: center;
          }
          
          .mc-nza-brand { 
            gap: 8px;
          }
          
          .mc-nza-brand img { 
            height: 28px;
          }
          
          .mc-nza-brand .divider { 
            height: 16px;
          }
          
          .facts {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 480px) {
          .hero-logos {
            margin-top: 16px;
          }
          
          .logo-showcase {
            flex-direction: column;
            gap: 12px;
            padding: 16px 20px;
          }
          
          .logo-divider {
            transform: rotate(90deg);
            width: 24px;
            height: 24px;
            font-size: 12px;
          }
          
          .company-logo {
            height: 36px;
          }
          
          .logo-label {
            font-size: 11px;
          }
          
          .hero-actions .btn {
            width: 100%;
          }
          
          .register-actions .btn {
            width: 100%;
          }
          
          .mc-nza-footwrap {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }
          
          .slot {
            flex-direction: column;
            gap: 4px;
          }
          
          .slot-time {
            min-width: auto;
          }
          
          .features {
            grid-template-columns: 1fr;
          }
          
          .agenda {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .slot {
            padding: 16px;
          }
          
          .slot-time {
            font-size: 15px;
          }
          
          .slot-content {
            font-size: 14px;
          }
          
          .faq {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .faq-item {
            padding: 20px;
          }
          
          .faq-question strong {
            font-size: 16px;
          }
          
          .faq-item p {
            font-size: 14px;
            padding-left: 24px;
          }
        }
      `}</style>
    </div>
  );
}

function Feature({ emoji, title, text }) {
  return (
    <div className="card feature">
      <div style={{ fontSize: 32, marginBottom: 8 }}>{emoji}</div>
      <h3>{title}</h3>
      <p className="muted">{text}</p>
    </div>
  );
}
function InfoCard({ label, value }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: "var(--accent)", marginBottom: 4 }}>{label}</div>
      <div className="muted" style={{ fontSize: 13 }}>{value}</div>
    </div>
  );
}
